import { isSubagentDefunctError } from "../../../../src/agents/subagent-defunct.js";
import type { ChatSubagentCard, ChatSubagentStatus } from "../ui-types.ts";

/** User-facing run phase (includes idle for restored / inactive sessions). */
export type SubagentActivityPhase =
  | "ongoing"
  | "completed"
  | "failed"
  | "defunct"
  | "idle"
  | "cancelled"
  // JAISIU-3558 — surface the future "blocked" phase on the wire as a
  // first-class activity phase so session-level status helpers
  // (`computeSessionStatus`) can rank it above `ongoing`. The wire
  // contract already allows `card.status === "blocked"` on agents that
  // surface a fleet-question pause; older agents that never emit it
  // still resolve to the same union they always did. The phase name
  // also leaves headroom for the upcoming `awaiting-input` and
  // `policy-gate` sub-phases without a union churn.
  | "blocked";

export type SubagentFleetSummary = {
  total: number;
  ongoing: number;
  completed: number;
  failed: number;
  defunct: number;
  idle: number;
  /** JAISIU-3319 / Pillar H: cancellation count, tracked separately from
   *  "failed" so the operator can see how many were halted by intent vs how
   *  many broke on their own. */
  cancelled: number;
  overall: "empty" | "in_progress" | "all_complete" | "needs_attention";
};

const ACTIVE = new Set<ChatSubagentStatus>([
  "queued",
  "spawning",
  "accepted",
  "cold-start",
  "running",
]);

export function isSubagentRunActive(status: ChatSubagentStatus): boolean {
  return ACTIVE.has(status);
}

/**
 * True while the run can still be cancelled by the operator.
 *
 * Wider than {@link isSubagentRunActive} by one state: a `blocked` run is
 * paused on a fleet question rather than progressing, and a paused run is
 * precisely the one an operator wants to cancel. Keep this the single
 * definition — the in-thread row and the fan-in banner previously encoded
 * it differently and disagreed about whether a blocked run was cancellable.
 */
export function isSubagentRunCancellable(status: ChatSubagentStatus): boolean {
  return ACTIVE.has(status) || status === "blocked";
}

/** True once the run has settled (`done` / `error` / `cancelled`). */
export function isSubagentRunTerminal(status: ChatSubagentStatus): boolean {
  return status === "done" || status === "error" || status === "cancelled";
}

/**
 * How long a child may sit in an active state with no progress before the
 * UI stops claiming the parent is still working.
 *
 * A completed plan left every card pinned at `running` because the terminal
 * transition and the plan-completion signal travel on different paths, so the
 * composer pill and the Fleet map kept advertising work that was already
 * over. There is no server-side reconciler for that, so the read side has to
 * refuse to trust an unbounded "running" on its own.
 */
const STALE_ACTIVE_MS = 120_000;

/**
 * True when the card is in an active state but has produced no observable
 * progress for longer than the staleness budget. Only applies to cards that
 * carry a `runId` (a genuinely tracked run): restored rows without one already
 * fall back to the phase heuristics in `resolveSubagentActivityPhase`.
 */
export function isSubagentRunStale(card: ChatSubagentCard, nowMs = Date.now()): boolean {
  if (!isSubagentRunActive(card.status)) {
    return false;
  }
  if (!card.runId?.trim()) {
    return false;
  }
  const updated = card.updatedAt ?? card.createdAt;
  if (typeof updated !== "number" || updated <= 0) {
    return false;
  }
  return nowMs - updated > STALE_ACTIVE_MS;
}

/**
 * Cards the run-activity surfaces should still advertise as in-flight.
 * Stale-but-active cards are dropped so a concluded plan stops presenting a
 * live subagent pill.
 */
export function filterActiveSubagents(cards: ChatSubagentCard[], nowMs = Date.now()): ChatSubagentCard[] {
  return cards.filter((card) => isSubagentRunActive(card.status) && !isSubagentRunStale(card, nowMs));
}

/** Infer display phase from card + lifecycle hints (restored rows often lack runId). */
export function resolveSubagentActivityPhase(card: ChatSubagentCard): SubagentActivityPhase {
  // JAISIU-3319 / Pillar H: cancellation gets its own phase so the summary
  // can count + headline it distinctly from genuine failures.
  if (card.status === "cancelled") {
    return "cancelled";
  }
  // JAISIU-3558 — surface the wire-level "blocked" status as a
  // first-class activity phase so callers like `computeSessionStatus`
  // can rank it above "ongoing" (the session is paused on a fleet
  // question, not actively progressing). The wire union predates this
  // but the activity-phase union catches up here.
  if (card.status === "blocked") {
    return "blocked";
  }
  if (card.status === "error" && isSubagentDefunctError(card.error)) {
    return "defunct";
  }
  if (card.status === "error") {
    return "failed";
  }
  if (card.status === "done") {
    // JAISIU-2110: defensive cross-check. If the backend says "done" but an
    // error is attached, the run likely ended without a clean outcome — promote
    // it to "failed" (or "defunct" if the registry marked it as such) so the
    // UI doesn't show a misleading green "completed" badge.
    const errorText = card.error?.trim();
    if (errorText) {
      return isSubagentDefunctError(errorText) ? "defunct" : "failed";
    }
    return "completed";
  }
  if (
    card.status === "queued" ||
    card.status === "spawning" ||
    card.status === "accepted" ||
    card.status === "cold-start"
  ) {
    return "ongoing";
  }
  const hasRun = Boolean(card.runId?.trim());
  const updated = card.updatedAt ?? card.createdAt;
  const staleMs = Date.now() - updated;
  if (!hasRun && card.childSessionKey?.trim()) {
    if (staleMs > 90_000 && !card.streamText?.trim()) {
      return "idle";
    }
    if (!card.toolHint?.trim() && !card.phase?.trim() && staleMs > 45_000) {
      return "idle";
    }
  }
  return "ongoing";
}

export function summarizeSubagentFleet(cards: ChatSubagentCard[]): SubagentFleetSummary {
  if (cards.length === 0) {
    return {
      total: 0,
      ongoing: 0,
      completed: 0,
      failed: 0,
      defunct: 0,
      idle: 0,
      cancelled: 0,
      overall: "empty",
    };
  }
  let ongoing = 0;
  let completed = 0;
  let failed = 0;
  let defunct = 0;
  let idle = 0;
  let cancelled = 0;
  for (const card of cards) {
    const phase = resolveSubagentActivityPhase(card);
    if (phase === "ongoing") {
      ongoing += 1;
    } else if (phase === "completed") {
      completed += 1;
    } else if (phase === "failed") {
      failed += 1;
    } else if (phase === "defunct") {
      defunct += 1;
    } else if (phase === "cancelled") {
      cancelled += 1;
    } else {
      idle += 1;
    }
  }
  let overall: SubagentFleetSummary["overall"] = "all_complete";
  if (ongoing > 0) {
    overall = "in_progress";
  } else if (failed > 0 || defunct > 0) {
    overall = "needs_attention";
  }
  return {
    total: cards.length,
    ongoing,
    completed,
    failed,
    defunct,
    idle,
    cancelled,
    overall,
  };
}

export function subagentPhaseLabel(phase: SubagentActivityPhase): string {
  switch (phase) {
    case "ongoing":
      return "In progress";
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    case "defunct":
      return "Defunct";
    case "cancelled":
      return "Cancelled";
    case "idle":
      return "Idle";
    default:
      return phase;
  }
}

/** Badge class suffix matching fleet-map-node__badge--* */
export function subagentPhaseBadgeClass(phase: SubagentActivityPhase): string {
  switch (phase) {
    case "ongoing":
      return "running";
    case "completed":
      return "done";
    case "failed":
      return "error";
    case "defunct":
      return "defunct";
    case "idle":
      return "idle";
    default:
      return "idle";
  }
}

export function fleetSummaryHeadline(summary: SubagentFleetSummary): string {
  if (summary.overall === "empty") {
    return "No subagent runs for this chat";
  }
  if (summary.overall === "in_progress") {
    const n = summary.ongoing;
    return n === 1 ? "1 task still in progress" : `${n} tasks still in progress`;
  }
  // JAISIU-3319 / Pillar H: cancellation bookkeeping. The headline must make
  // it obvious that an operator stopped something — silent "All N done"
  // reads as success when half the fleet was cancelled.
  const cancelSuffix =
    summary.cancelled > 0
      ? summary.cancelled === 1
        ? ", 1 cancelled"
        : `, ${summary.cancelled} cancelled`
      : "";
  if (summary.overall === "needs_attention") {
    if (summary.defunct > 0 && summary.failed === 0) {
      return summary.defunct === 1
        ? `All tasks stopped — 1 defunct (lost after restart)${cancelSuffix}`
        : `All tasks stopped — ${summary.defunct} defunct (lost after restart)${cancelSuffix}`;
    }
    if (summary.defunct > 0) {
      return `All tasks stopped — ${summary.failed} failed, ${summary.defunct} defunct${cancelSuffix}`;
    }
    return summary.failed === 1
      ? `All tasks stopped — 1 failed${cancelSuffix}`
      : `All tasks stopped — ${summary.failed} failed${cancelSuffix}`;
  }
  const finished = summary.completed + summary.idle;
  if (summary.cancelled > 0) {
    return `All ${summary.total} tasks settled (${summary.completed} done, ${summary.cancelled} cancelled, ${summary.failed + summary.defunct} failed)`;
  }
  if (summary.idle > 0 && summary.completed === 0) {
    return finished === 1 ? "All tasks idle" : `All ${finished} subagents idle`;
  }
  if (summary.idle > 0) {
    return `All tasks complete (${summary.completed} done, ${summary.idle} idle)`;
  }
  return summary.total === 1 ? "All tasks complete" : `All ${summary.total} tasks complete`;
}
